export interface RolePermission {
  id: string;
  tenantId: string;
  action: string;
  key: string;
  resource: string;
  createdAt: string;
  updatedAt: string;
}

export interface StaffRole {
  id: string;
  tenantId: string;
  key: string;
  name: string;
  description: string;
  canBeScoped: boolean;
  icon: string | null;
  createdAt: string;
  updatedAt: string;
  rolePermissions: RolePermission[];
}

export interface StaffRoleAssignment {
  id: string;
  tenantId: string;
  staffProfileId: string;
  staffRoleId: string;
  assignedBy: string;
  assignmentReason: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  staffRole: StaffRole;
}

export interface StaffProfile {
  id: string;
  tenantId: string;
  userId: string;
  staffId: string;
  employeeId: string | null;
  hireDate: string;
  departmentId: string | null;
  staffInvitationId: string | null;
  invitationStatus: string | null;
  jobTitle: string | null;
  managerId: string | null;
  metadata: {
    alternateEmailVerified?: boolean;
    smsNotifications?: boolean;
    pushNotifications?: boolean;
    emailNotifications?: boolean;
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
  department: any | null;
  staffRoleAssignments: StaffRoleAssignment[];
}

export interface StudentProfile {
  id: string;
  userId: string;
  departmentId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  /** Combined name when returned by list/detail APIs. */
  fullName?: string;
  displayName?: string;
  avatar?: string;
  avatarFile?: {
    id: string;
    url: string;
    secureUrl: string;
    [key: string]: any;
  };
  role: UserRole;
  accountType?: string;
  isVerified: boolean;
  staffProfile?: StaffProfile;
  createdAt: string;
  updatedAt: string;
}

export enum UserRole {
  ADMIN = 'admin',
  INSTRUCTOR = 'instructor',
  LEARNER = 'learner',
}

// Authentication Types
export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: UserRole;
}

// Course Types
import type { CourseStatus } from '@/constants/course-status';

export type { CourseStatus };

export interface CourseInstructor {
  id: string;
  name: string;
  email?: string;
  isPrimary?: boolean;
}

export interface Course {
  id: string;
  title: string;
  /** Course code from API (e.g. GDM-924). */
  code?: string;
  description: string;
  thumbnail?: string;
  price: number | string;
  currency: string;
  discountPrice?: number | string;
  effectiveDate?: string;
  level: CourseLevel | string;
  category?: string;
  categoryId?: string;
  /** @deprecated Prefer programmeIds / programmes[] */
  programmeId?: string;
  /** Programme IDs to sync on create/update */
  programmeIds?: string[];
  programmes?: CourseProgrammeLink[];
  tags?: string[];
  instructorIds?: string[];
  instructors?: CourseInstructor[];
  totalLessons?: number;
  totalDuration?: number; // in minutes
  rating: number;
  enrollmentCount: number;
  studentCount?: number;
  lessonCount?: number;
  publishedLessonCount?: number;
  archiveReason?: string | null;
  archivedAt?: string | null;
  status: CourseStatus | 'pending';
  isPublished?: boolean; // Deprecated, keep for backward compatibility if needed
  shortDescription?: string;
  coverImage?: string;
  /** Resolved cover image URL from API when available. */
  coverImageUrl?: string;
  /** API may return a human-readable duration string (e.g. "64 Hours"). */
  duration?: string;
  isFree?: boolean;
  /** When true, course is listed on the application portal. */
  visibleOnApplicationPortal?: boolean;
  /** How learners enroll: request approval or enroll directly. */
  enrollmentMode?: 'request' | 'direct';
  durationHours?: number;
  maxStudents?: number;
  prerequisites?: string;
  learningObjectives?: string[];
  /** Rich-text / HTML fields used by some create/update payloads */
  learningObjective?: string;
  requiredMaterials?: string;
  taskStructure?: string;
  lessonOutcome?: string;
  competencies?: { title: string; description: string }[];
  /** Programme division for a single linked programme. Null clears it on update. */
  divisionId?: string | null;
  coverImageFileId?: string;
  slug?: string;
  language?: string;
  createdAt: string;
  updatedAt: string;
  modules?: CourseModule[];
}

export interface CourseProgrammeLink {
  programmeId: string;
  programmeTitle: string;
  credits?: number;
  divisionId?: string;
  divisionName?: string;
}

export interface Category {
  id: string;
  name: string;
  code?: string;
  slug: string;
  type?: string;
  parentId?: string;
  description: string;
  isActive?: boolean;
  courseCount?: number;
  color?: string;
  icon?: string;
  createdAt?: string;
  updatedAt?: string;
}

export enum CourseLevel {
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate',
  ADVANCED = 'advanced',
}

export interface CourseModule {
  id: string;
  title: string;
  description: string;
  order: number;
  lessons: Lesson[];
}

export interface Lesson {
  id: string;
  title: string;
  description: string;
  content: string;
  videoUrl?: string;
  duration: number; // in minutes
  order: number;
  moduleId: string;
  attachments: Attachment[];
  quiz?: Quiz;
}

/** Uploadable / managed file attached to a staff lesson (`/staff/lessons/:id/materials`). */
export type StaffLessonMaterialType =
  | 'presentation'
  | 'document'
  | 'video'
  | 'audio'
  | 'image'
  | 'link'
  | 'other'
  | string;

export type StaffLessonMaterialUploadStatus = 'pending' | 'uploading' | 'completed' | 'failed' | string;

/** Staff curriculum lesson from `GET/POST/PATCH /staff/lessons` (course-level; not module `Lesson`). */
export interface StaffLessonMaterial {
  id: string;
  lessonId: string;
  courseId?: string;
  name: string;
  materialCategory?: string;
  assetCategory?: string;
  materialType?: StaffLessonMaterialType;
  fileId?: string | null;
  orderIndex: number;
  isDownloadable: boolean;
  isRequired: boolean;
  description?: string | null;
  metadata?: Record<string, unknown> | null;
  uploadStatus?: StaffLessonMaterialUploadStatus;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  tenantId?: string;
}

export interface StaffLessonCompletionCriteria {
  type: string;
  value?: number;
  required?: boolean;
}

export interface StaffLesson {
  id: string;
  tenantId?: string;
  courseId: string;
  sectionId?: string | null;
  title: string;
  description?: string | null;
  content?: string | null;
  orderIndex: number;
  /** Primary type field from API */
  lessonType: string;
  /** Some responses use `classType` instead of `lessonType` */
  classType?: string;
  videoUrl?: string | null;
  durationMinutes?: number;
  date?: string | null;
  timeStart?: string | null;
  timeEnd?: string | null;
  meetLink?: string | null;
  recordingFileId?: string | null;
  liveSessionPlatform?: string | null;
  isPublished: boolean;
  metadata?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  /** Nested course summary when returned by list endpoints */
  course?: {
    id?: string;
    title?: string;
    instructors?: CourseInstructor[];
  } | null;
  materials?: StaffLessonMaterial[];
  videoDuration?: number;
  isPreview?: boolean;
  completionCriteria?: StaffLessonCompletionCriteria | null;
}

/**
 * Create body for `POST /staff/lessons`.
 * Some backends accept `classType` on create while responses use `lessonType`.
 */
export interface CreateStaffLessonPayload {
  courseId: string;
  title: string;
  description?: string;
  content?: string | null;
  sectionId?: string;
  orderIndex?: number;
  durationMinutes?: number;
  isPublished?: boolean;
  metadata?: Record<string, unknown>;
  isPreview?: boolean;
  videoUrl?: string | null;
  videoDuration?: number;
  completionCriteria?: StaffLessonCompletionCriteria;
  meetLink?: string | null;
  liveSessionPlatform?: string | null;
  /** Single ISO datetime (create sample) */
  datetime?: string;
  /** Or split schedule fields (list/detail shape) */
  date?: string;
  timeStart?: string;
  timeEnd?: string;
  classType?: string;
  lessonType?: string;
}

export type UpdateStaffLessonPayload = Partial<
  Omit<CreateStaffLessonPayload, 'courseId'>
> & {
  title?: string;
  description?: string | null;
  content?: string | null;
};

/** `POST /staff/lessons/reorder` — ordered lesson UUIDs. */
export interface ReorderStaffLessonsPayload {
  lessonIds: string[];
}

export interface StaffLessonMaterialsListResult {
  data: StaffLessonMaterial[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasMore?: boolean;
  };
}

export interface GetStaffLessonMaterialsParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface CreateStaffLessonMaterialRequest {
  name: string;
  materialType: StaffLessonMaterialType;
  fileId?: string;
  orderIndex?: number;
  isDownloadable?: boolean;
  isRequired?: boolean;
  description?: string;
  metadata?: Record<string, unknown>;
  uploadStatus?: StaffLessonMaterialUploadStatus;
}

export interface UpdateStaffLessonMaterialRequest {
  name?: string;
  isDownloadable?: boolean;
  isRequired?: boolean;
  description?: string;
  orderIndex?: number;
  metadata?: Record<string, unknown>;
}

export interface Attachment {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
}

// Quiz Types
export interface Quiz {
  id: string;
  title: string;
  description: string;
  questions: Question[];
  passingScore: number;
  timeLimit?: number; // in minutes
}

export interface Question {
  id: string;
  text: string;
  type: QuestionType;
  options: QuestionOption[];
  correctAnswer: string | string[];
  explanation?: string;
}

export enum QuestionType {
  MULTIPLE_CHOICE = 'multiple_choice',
  TRUE_FALSE = 'true_false',
  SHORT_ANSWER = 'short_answer',
}

export interface QuestionOption {
  id: string;
  text: string;
  isCorrect: boolean;
}

// Progress Types
export interface UserProgress {
  id: string;
  userId: string;
  courseId: string;
  completedLessons: string[];
  completedQuizzes: string[];
  overallProgress: number; // percentage
  lastAccessedAt: string;
  completedAt?: string;
}

// Enrollment Types
export enum ENROLLMENT_TYPE {
  COURSE = "course",
  PROGRAMME = "programme",
}

export type EnrollmentLifecycleStatus =
  | 'active'
  | 'completed'
  | 'cancelled'
  | 'suspended'
  | 'dropped'
  | string;

export interface EnrollmentLessonProgress {
  id: string;
  lessonId?: string;
  status: string;
  completionPercentage?: number | string | null;
  timeSpentMinutes?: number;
  startedAt?: string | null;
  completedAt?: string | null;
  lastAccessedAt?: string | null;
  lesson?: {
    id: string;
    title: string;
    orderIndex?: number;
    lessonType?: string;
    durationMinutes?: number;
  };
}

export interface EnrollmentProgramme {
  id: string;
  title: string;
  description?: string | null;
  shortDescription?: string | null;
  durationMonths?: number | null;
}

export interface Enrollment {
  id: string;
  tenantId?: string;
  userId: string;
  /** When present, matches `Student.id` for the enrolled learner. */
  studentId?: string | null;
  courseId: string;
  sessionId?: string | null;
  programmeId?: string | null;
  /** Present on detail fetch; list responses may omit nested course. */
  course?: Course;
  /** Present on detail fetch for programme enrollments. */
  programme?: EnrollmentProgramme | null;
  enrollmentType?: string;
  enrolledAt: string;
  startedAt?: string | null;
  completedAt?: string | null;
  expiresAt?: string | null;
  status: EnrollmentLifecycleStatus;
  progressPercentage: number | string;
  timeSpentMinutes?: number;
  lastAccessedAt?: string | null;
  enrolledBy?: string | null;
  paymentStatus?: string;
  paymentAmount?: number | string | null;
  notes?: string | null;
  metadata?: unknown | null;
  createdAt?: string;
  updatedAt?: string;
  user?: User;
  student?: User;
}

export interface EnrollmentDetail extends Enrollment {
  course?: Course;
  progress?: EnrollmentLessonProgress[];
  certificates?: unknown[];
}

export interface EnrollmentStatistics {
  period: string;
  total: number;
  byStatus: {
    active: number;
    completed: number;
    cancelled: number;
    suspended: number;
  };
  averageProgress: number;
  totalTimeSpent: number;
}

export interface CreateEnrollmentPayload {
  userId: string;
  courseId?: string;
  programmeId?: string;
  enrollmentType?: ENROLLMENT_TYPE;
  paymentAmount?: number | string;
  sessionId?: string;
  paymentStatus?: string;
  expiresAt?: string;
  status?: string;
  enrollmentDate: string;
  notes?: string;
}

export interface UpdateEnrollmentPayload {
  status?: string;
  notes?: string;
  courseId?: string | null;
  programmeId?: string | null;
  sessionId?: string | null;
  paymentAmount?: number | string;
  paymentStatus?: string;
  expiresAt?: string;
}

export interface BulkEnrollmentRowPayload {
  userId: string;
  courseId?: string;
  programmeId?: string;
  sessionId?: string;
  paymentStatus?: string;
  paymentAmount?: number;
  notes?: string;
}

/** Staff enrollment request list/detail (GET /staff/enrollment-requests). */
export interface EnrollmentRequestStudentProfile {
  id: string;
  tenantId?: string;
  userId: string;
  studentId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  user?: User;
}

export type StudentApplicationStatus =
  | 'draft'
  | 'submitted'
  | 'in_review'
  | 'accepted'
  | 'rejected'
  | 'waitlisted'
  | 'withdrawn';

export interface StudentApplicationPersonalDetails {
  city?: string;
  email?: string;
  phone?: string;
  address?: string;
  country?: string;
  lastName?: string;
  firstName?: string;
  dateOfBirth?: string;
}

export interface StudentApplicationProgramSelection {
  campus?: string;
  courseId?: string;
  programId?: string;
  intakeId?: string;
  studyMode?: string;
}

export interface StudentApplicationEducationBackground {
  grade?: string;
  institution?: string;
  qualification?: string;
  graduationYear?: string;
}

export interface StudentApplicationAdditionalInformation {
  sponsorship?: string;
  personalStatement?: string;
  accessibilityNeeds?: string;
  [key: string]: string | undefined;
}

export interface StudentApplicationDocument {
  id: string;
  name: string;
  fileId?: string;
  status: 'uploaded' | 'not_uploaded' | string;
  fileUrl?: string;
  fileName?: string;
  required: boolean;
  updatedAt?: string;
  description?: string;
}

export interface StudentApplication {
  id: string;
  tenantId?: string;
  userId: string;
  reference: string;
  status: StudentApplicationStatus | string;
  personalDetails?: StudentApplicationPersonalDetails | null;
  programSelection?: StudentApplicationProgramSelection | null;
  educationBackground?: StudentApplicationEducationBackground | null;
  additionalInformation?: StudentApplicationAdditionalInformation | null;
  documents?: StudentApplicationDocument[];
  completionPercentage: number;
  submittedAt?: string | null;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  reviewNotes?: string | null;
  createdAt: string;
  updatedAt: string;
  isDraft?: boolean;
  isSubmitted?: boolean;
  isAccepted?: boolean;
  isRejected?: boolean;
  user?: User;
  reviewer?: User | null;
}

export interface StudentApplicationStatistics {
  total: number;
  draft: number;
  submitted: number;
  inReview: number;
  accepted: number;
  rejected: number;
  waitlisted: number;
  withdrawn: number;
}

export type ApplicationWaitlistStatus = 'pending' | 'invited' | 'converted' | 'archived';

export interface ApplicationWaitlistEntry {
  id: string;
  tenantId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string | null;
  programOfInterest?: string | null;
  message?: string | null;
  status: ApplicationWaitlistStatus | string;
  invitedAt?: string | null;
  invitedBy?: string | null;
  archivedAt?: string | null;
  convertedAt?: string | null;
  convertedUserId?: string | null;
  staffNotes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApplicationWaitlistStatistics {
  pending: number;
  invited: number;
  converted: number;
  archived: number;
  total: number;
}

export interface EnrollmentRequest {
  id: string;
  tenantId?: string;
  userId: string;
  courseId?: string | null;
  programmeId?: string | null;
  enrollmentType?: string;
  status: string;
  requestedAt: string;
  approvedAt?: string | null;
  approvedBy?: string | null;
  declinedAt?: string | null;
  declinedBy?: string | null;
  declineReason?: string | null;
  expiresAt?: string | null;
  cancelledAt?: string | null;
  notes?: string | null;
  metadata?: unknown | null;
  createdAt?: string;
  updatedAt?: string;
  course?: Course;
  student?: User;
  studentProfile?: EnrollmentRequestStudentProfile;
  user?: User;
  approver?: User | null;
  decliner?: User | null;
}

// Payment Types
export interface Payment {
  id: string;
  userId: string;
  courseId: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  paymentMethod: string;
  transactionId: string;
  createdAt: string;
}

export enum PaymentStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  REFUNDED = 'refunded',
  CANCELLED = 'cancelled',
}

// Certificate Types
export interface Certificate {
  id: string;
  userId: string;
  courseId: string;
  certificateUrl: string;
  issuedAt: string;
}

// API Response Types
export interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Common UI Types
export interface SelectOption {
  value: string;
  label: string;
}

export interface TableColumn {
  key: string;
  title: string;
  sortable?: boolean;
  width?: string;
}

export interface NotificationData {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
}

// Analytics Types
export interface TrendPoint {
  date: string;
  value: number;
}

export interface CoursePerformance {
  enrollmentTrend: TrendPoint[];
  revenueTrend: TrendPoint[];
  completionTrend: TrendPoint[];
  totalEnrollments: number;
  totalRevenue: number;
  averageCompletion: number;
}

/** Nested author on GET /staff/announcements/:id */
export interface AnnouncementAuthor {
  id: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  [key: string]: unknown;
}

/** Nested category on GET /staff/announcements/:id */
export interface AnnouncementCategory {
  id: string;
  tenantId?: string;
  name: string;
  slug: string;
  displayOrder?: number;
  allowInstructorCreate?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Announcement {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  tenantId: string;
  title: string;
  slug?: string | null;
  content: string;
  bannerFileId?: string | null;
  blocks?: unknown | null;
  type: 'institution_wide' | 'role_based' | 'department' | 'course';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'draft' | 'published' | 'archived';
  authorId: string;
  publishedAt?: string | null;
  scheduledFor?: string | null;
  expiresAt?: string | null;
  targetRoles?: string[] | null;
  targetDepartments?: string[] | null;
  targetCourses?: string[] | null;
  categoryId?: string | null;
  isEmailNotification: boolean;
  emailTemplateId?: string | null;
  templateVariables?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  /** Present on detail/list when API embeds relation */
  author?: AnnouncementAuthor | null;
  category?: AnnouncementCategory | null;
}

// Department Types
export interface Department {
  id: string;
  name: string;
  code: string;
  description: string;
  headUserId?: string | null;
  head?: User | null;
  isActive: boolean;
  staffProfiles?: StaffProfile[];
  createdAt: string;
  updatedAt: string;
  staffCount?: number;
  studentCount?: number;
  meta?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}